import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Phone, Loader } from 'lucide-react';
import { API_ENDPOINTS, fetchWithTimeout } from '../config/api';

interface FormData {
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  user_phone: string;
  user_password: string;
  confirmPassword: string;
}

interface FormErrors {
  user_first_name?: string;
  user_last_name?: string;
  user_email?: string;
  user_phone?: string;
  user_password?: string;
  confirmPassword?: string;
}

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    user_first_name: '',
    user_last_name: '',
    user_email: '',
    user_phone: '',
    user_password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    if (!formData.user_first_name.trim()) {
      errors.user_first_name = 'First name is required';
      isValid = false;
    }

    if (!formData.user_last_name.trim()) {
      errors.user_last_name = 'Last name is required';
      isValid = false;
    }

    if (!formData.user_email.trim()) {
      errors.user_email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.user_email)) {
      errors.user_email = 'Please enter a valid email address';
      isValid = false;
    }

    if (!formData.user_phone.trim()) {
      errors.user_phone = 'Phone number is required';
      isValid = false;
    } else if (!/^(?:\+254|0)[17]\d{8}$/.test(formData.user_phone)) {
      errors.user_phone = 'Please enter a valid Kenyan phone number';
      isValid = false;
    }

    if (!formData.user_password) {
      errors.user_password = 'Password is required';
      isValid = false;
    } else if (formData.user_password.length < 6) {
      errors.user_password = 'Password must be at least 6 characters long';
      isValid = false;
    }

    if (formData.user_password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const { confirmPassword, ...submitData } = formData;
      const formDataToSend = new URLSearchParams();
      
      // Add all required fields
      formDataToSend.append('user_first_name', submitData.user_first_name.trim());
      formDataToSend.append('user_last_name', submitData.user_last_name.trim());
      formDataToSend.append('user_email', submitData.user_email.trim());
      formDataToSend.append('user_phone', submitData.user_phone.trim());
      formDataToSend.append('user_password', submitData.user_password);
      formDataToSend.append('retailer_code', '0000');

      const response = await fetchWithTimeout(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        mode: 'cors',
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success) {
        navigate('/login', { 
          state: { 
            message: data.message || 'Registration successful! Please sign in to continue.' 
          }
        });
      } else {
        setError(data.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      const error = err as Error;
      if (error.message === 'Request timeout') {
        setError('Connection timed out. Please try again.');
      } else if (error.message.includes('Failed to fetch')) {
        setError('Unable to connect to the server. Please check your internet connection.');
      } else {
        setError('Network error. Please check your connection and try again.');
      }
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-green-600 hover:text-green-500 dark:text-green-400 dark:hover:text-green-300"
            >
              Sign in
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
              <div className="text-sm text-red-700 dark:text-red-200">{error}</div>
            </div>
          )}

          <div className="rounded-md shadow-sm space-y-4">
            {/* First Name */}
            <div>
              <label htmlFor="user_first_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="user_first_name"
                  name="user_first_name"
                  type="text"
                  required
                  value={formData.user_first_name}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-3 py-2 pl-10 border ${
                    formErrors.user_first_name 
                      ? 'border-red-300 dark:border-red-700' 
                      : 'border-gray-300 dark:border-gray-600'
                  } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm dark:bg-gray-700`}
                  placeholder="First Name"
                />
              </div>
              {formErrors.user_first_name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.user_first_name}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="user_last_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="user_last_name"
                  name="user_last_name"
                  type="text"
                  required
                  value={formData.user_last_name}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-3 py-2 pl-10 border ${
                    formErrors.user_last_name 
                      ? 'border-red-300 dark:border-red-700' 
                      : 'border-gray-300 dark:border-gray-600'
                  } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm dark:bg-gray-700`}
                  placeholder="Last Name"
                />
              </div>
              {formErrors.user_last_name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.user_last_name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="user_email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="user_email"
                  name="user_email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.user_email}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-3 py-2 pl-10 border ${
                    formErrors.user_email 
                      ? 'border-red-300 dark:border-red-700' 
                      : 'border-gray-300 dark:border-gray-600'
                  } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm dark:bg-gray-700`}
                  placeholder="Email address"
                />
              </div>
              {formErrors.user_email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.user_email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="user_phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="user_phone"
                  name="user_phone"
                  type="tel"
                  required
                  value={formData.user_phone}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-3 py-2 pl-10 border ${
                    formErrors.user_phone 
                      ? 'border-red-300 dark:border-red-700' 
                      : 'border-gray-300 dark:border-gray-600'
                  } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm dark:bg-gray-700`}
                  placeholder="e.g., 0712345678"
                />
              </div>
              {formErrors.user_phone && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.user_phone}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="user_password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="user_password"
                  name="user_password"
                  type="password"
                  required
                  value={formData.user_password}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-3 py-2 pl-10 border ${
                    formErrors.user_password 
                      ? 'border-red-300 dark:border-red-700' 
                      : 'border-gray-300 dark:border-gray-600'
                  } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm dark:bg-gray-700`}
                  placeholder="Password"
                />
              </div>
              {formErrors.user_password && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.user_password}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-3 py-2 pl-10 border ${
                    formErrors.confirmPassword 
                      ? 'border-red-300 dark:border-red-700' 
                      : 'border-gray-300 dark:border-gray-600'
                  } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm dark:bg-gray-700`}
                  placeholder="Confirm Password"
                />
              </div>
              {formErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.confirmPassword}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader className="animate-spin h-5 w-5" />
              ) : (
                'Create Account'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;